import { Paper } from "@mui/material";
import type { ColDef, ColTypeDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useSectionSpecTableData } from "../../hooks/useSectionSpecTableData";
import { navigateSectionSpecDimension } from "../../models/SectionSpecDimensionNavigation";
import type {
	SpecBandField,
	SpecItemDraftRow,
} from "../../models/SectionSpecForm";

interface SectionSpecDimensionsGridProps {
	rows: SpecItemDraftRow[];
	isLoading: boolean;
	readOnly: boolean;
	scrollable?: boolean;
	onDegreesChange?: (profileItemId: number, isDegrees: boolean) => void;
	angleInputs?: boolean;
	showType?: boolean;
	showDegrees?: boolean;
	showBands?: boolean;
	getError?: (
		profileItemId: number,
		field: SpecBandField,
	) => string | undefined;
	getToggleLock?: (row: SpecItemDraftRow) => string | undefined;
	onGridApiChange?: (api: GridApi<SpecItemDraftRow> | null) => void;
	onToggle: (profileItemId: number, applies: boolean) => void;
	onEdit: (profileItemId: number, field: SpecBandField, value: string) => void;
}

export const SectionSpecDimensionsGrid = ({
	rows,
	isLoading,
	readOnly,
	scrollable = false,
	onDegreesChange,
	angleInputs = false,
	showType = false,
	showDegrees = true,
	showBands = true,
	getError,
	getToggleLock,
	onGridApiChange,
	onToggle,
	onEdit,
}: SectionSpecDimensionsGridProps) => {
	const gridApiRef = useRef<GridApi<SpecItemDraftRow> | null>(null);
	const { columnDefs } = useSectionSpecTableData({
		onDegreesChange,
		angleInputs,
		showType,
		showDegrees,
		showBands,
		getError,
		getToggleLock,
		onEdit,
		onToggle,
		readOnly,
	});
	const { defaultColDef, columnTypes } = useGridDefinitions({});

	return (
		<Paper
			variant="outlined"
			onKeyDownCapture={(event) =>
				navigateSectionSpecDimension(
					event.nativeEvent,
					gridApiRef.current,
					readOnly,
				)
			}
			sx={{
				width: "100%",
				height: scrollable
					? { xs: "clamp(240px, 45vh, 480px)", md: "100%" }
					: undefined,
				flex: scrollable ? { md: 1 } : undefined,
				minHeight: 0,
				...(scrollable && {
					"@media (max-height: 600px)": {
						height: "clamp(240px, 45vh, 480px)",
						flex: "none",
					},
				}),
				overflow: "hidden",
				"& .ag-cell": {
					color: "text.primary",
				},
				"&& .section-spec-cell-error": {
					boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.error.main}`,
				},
				"&& .ag-cell-inline-editing": {
					bgcolor: "background.paper",
					boxShadow: "inset 0 0 0 1px",
					boxShadowColor: "primary.main",
					height: "100%",
					minHeight: 0,
					p: "0 4px",
				},
				"& .ag-cell-inline-editing .ag-input-field-input": {
					bgcolor: "transparent",
					border: 0,
					borderRadius: 0,
					boxSizing: "border-box",
					boxShadow: "none",
					color: "text.primary",
					height: "100%",
					minHeight: 0,
					outline: "none",
					px: 0,
					textAlign: "center",
					width: "100%",
				},
			}}
		>
			<AgGridReact<SpecItemDraftRow>
				domLayout={scrollable ? "normal" : "autoHeight"}
				loading={isLoading}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={{
					...(defaultColDef as ColDef<SpecItemDraftRow>),
					lockVisible: true,
				}}
				suppressMovableColumns
				suppressDragLeaveHidesColumns
				columnTypes={
					columnTypes as Record<string, ColTypeDef<SpecItemDraftRow>>
				}
				rowHeight={32}
				headerHeight={34}
				animateRows
				singleClickEdit
				stopEditingWhenCellsLoseFocus
				tooltipShowDelay={0}
				onGridReady={(event) => {
					gridApiRef.current = event.api;
					onGridApiChange?.(event.api);
				}}
				onGridPreDestroyed={() => {
					gridApiRef.current = null;
					onGridApiChange?.(null);
				}}
				getRowId={(params) => String(params.data.ProfileItemId)}
				loadingOverlayComponent={CenteredLazyLoading}
			/>
		</Paper>
	);
};
